import React from 'react'
import { isVCSubdomain } from '../utils/data'
import { ChainId, CURRENT_CHAIN, LAUNCHER_ADDRESS, MASTERCHIEF_ADDRESS, XHSPX_ADDRESS } from '@hyperswap/addresses'

type Link = {
  name: string
  link: string
  logo: JSX.Element
}

type Links = {
  landingPage: Link
  twitter: Link
  discord: Link
  github: Link
  docs: Link
}

export type LinkKeys = keyof typeof LINKS

interface BaseRoute {
  name: string
}
interface SimpleRoute extends BaseRoute {
  route: string
  routeMatches: string[]
  internal: boolean
  disabled?: boolean
}
export interface RouteWithChildren extends BaseRoute {
  childrens: SimpleRoute[]
}

type RouteItem = SimpleRoute | RouteWithChildren

export const IS_TESTNET = (CURRENT_CHAIN as unknown) === ChainId.HYPEREVM_TESTNET

export const API_POOL_BASE_URL = 'https://api.hyperswap.exchange'
export const API_POOL_INDEXED_BASE_URL = 'https://indexer-7afbf28eaa4a.herokuapp.com'
export const API_GASLESS_BASE_URL = IS_TESTNET
  ? 'https://relay-gasless-3c04fa893f2c.herokuapp.com'
  : 'https://relay-gasless-mainnet-7c5befda551e.herokuapp.com'
export const API_GRAPHQL_STHYPE = 'https://index.stakedhype.fi/graphql'
export const VC_SUBDOMAIN = 'vc'
export const TESTNET_SUBDOMAIN = 'testnet'

export const FARM_IS_ENABLE = MASTERCHIEF_ADDRESS && MASTERCHIEF_ADDRESS.length > 0
export const REDEEM_IS_ENABLE = XHSPX_ADDRESS && XHSPX_ADDRESS.length > 0
export const LAUNCHER_IS_ENABLE = LAUNCHER_ADDRESS && LAUNCHER_ADDRESS.length > 0

export const ROUTES: RouteItem[] = !isVCSubdomain()
  ? [
      {
        name: 'Swap',
        route: '/swap',
        routeMatches: ['/swap'],
        internal: true,
      },
      {
        name: 'Pools',
        childrens: [
          {
            name: 'View positions',
            route: '/pool',
            routeMatches: ['/pool', '/remove', '/increase', 'find', 'migrate'],
            internal: true,
          },
          {
            name: 'Create position',
            route: '/add/v2/HYPE',
            routeMatches: ['/add/v2/HYPE'],
            internal: true,
          },
        ],
      },
      {
        name: 'Explore',
        route: '/explore/explore-pools',
        routeMatches: ['/explore/explore-pools'],
        internal: true,
      },
      /* {
        name: 'Explore',
        childrens: [
          {
            name: 'Pools',
            route: '/explore/explore-pools',
            routeMatches: ['/explore/explore-pools'],
            internal: true,
          },
        ],
      }, */
      /* {
        name: 'Earn',
        childrens: [
          {
            name: 'Farm',
            route: '/farm',
            routeMatches: ['/farm'],
            internal: true,
            disabled: !FARM_IS_ENABLE,
          },
          {
            name: 'Redeem',
            route: '/redeem',
            routeMatches: ['/redeem'],
            internal: true,
            disabled: !REDEEM_IS_ENABLE,
          },
          {
            name: 'Stake',
            route: '/stake',
            routeMatches: ['/stake'],
            internal: true,
          },
        ],
      }, */
      /*    {
        name: 'Liquidity Hub',
        route: '/liquidity-hub',
        routeMatches: ['/liquidity-hub'],
        internal: true,
       }, */
      {
        name: 'Launcher',
        route: '/launcher',
        routeMatches: ['/launcher'],
        internal: true,
        disabled: !LAUNCHER_IS_ENABLE,
      },
      {
        name: 'Points',
        route: '/points',
        routeMatches: ['/points'],
        internal: true,
      },
      /* {
        name: 'More',
        childrens: [
          {
            name: 'Launcher',
            route: '/launcher',
            routeMatches: ['/launcher'],
            internal: true,
            disabled: !LAUNCHER_IS_ENABLE,
          },
          ...(isTestnetSubdomain()
            ? [
                {
                  name: 'Testnet',
                  route: '/testnet',
                  routeMatches: ['/testnet'],
                  internal: true,
                },
              ]
            : []),
        ],
      }, */
    ]
  : [
      {
        name: 'VC Dashboard',
        route: '/vc-dashboard',
        routeMatches: ['/vc-dashboard'],
        internal: true,
      },
    ]

export const LINKS: Links = {
  landingPage: {
    name: 'Website',
    link: 'https://hyperswap.exchange/',
    logo: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
        <path d="M3.6 9h16.8" />
        <path d="M3.6 15h16.8" />
        <path d="M11.5 3a17 17 0 0 0 0 18" />
        <path d="M12.5 3a17 17 0 0 1 0 18" />
      </svg>
    ),
  },
  twitter: {
    name: 'X',
    link: 'https://twitter.com/HyperSwapX',
    logo: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M4 4l11.733 16h4.267l-11.733 -16z" />
        <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772" />
      </svg>
    ),
  },
  discord: {
    name: 'Discord',
    link: 'https://discord.com/invite/hyperswap',
    logo: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M8 12a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
        <path d="M14 12a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
        <path d="M15.5 17c0 1 1.5 3 2 3c1.5 0 2.833 -1.667 3.5 -3c.667 -1.667 .5 -5.833 -1.5 -11.5c-1.457 -1.015 -3 -1.34 -4.5 -1.5l-.972 1.923a11.913 11.913 0 0 0 -4.053 0l-.975 -1.923c-1.5 .16 -3.043 .485 -4.5 1.5c-2 5.667 -2.167 9.833 -1.5 11.5c.667 1.333 2 3 3.5 3c.5 0 2 -2 2 -3" />
        <path d="M7 16.5c3.5 1 6.5 1 10 0" />
      </svg>
    ),
  },
  github: {
    name: 'Github',
    link: 'https://github.com/HyperSwapX',
    logo: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M9 19c-4.3 1.4 -4.3 -2.5 -6 -3m12 5v-3.5c0 -1 .1 -1.4 -.5 -2c2.8 -.3 5.5 -1.4 5.5 -6a4.6 4.6 0 0 0 -1.3 -3.2a4.2 4.2 0 0 0 -.1 -3.2s-1.1 -.3 -3.5 1.3a12.3 12.3 0 0 0 -6.2 0c-2.4 -1.6 -3.5 -1.3 -3.5 -1.3a4.2 4.2 0 0 0 -.1 3.2a4.6 4.6 0 0 0 -1.3 3.2c0 4.6 2.7 5.7 5.5 6c-.6 .6 -.6 1.2 -.5 2v3.5" />
      </svg>
    ),
  },
  docs: {
    name: 'Docs',
    link: 'https://docs.hyperswap.exchange',
    logo: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M19 4v16h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12z" />
        <path d="M19 16h-12a2 2 0 0 0 -2 2" />
        <path d="M9 8h6" />
      </svg>
    ),
  },
}
